// 懒加载工具类
class LazyLoader {
  constructor() {
    this.observer = null;
    this.init();
  }

  // 初始化 Intersection Observer
  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadElement(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px 0px', // 提前50px开始加载
        threshold: 0.1
      });
    }
  }

  // 加载元素
  loadElement(element) {
    const type = element.dataset.lazyType;
    
    switch (type) {
      case 'image':
        this.loadImage(element);
        break;
      case 'video':
        this.loadVideo(element);
        break;
      case 'background':
        this.loadBackground(element);
        break;
      default:
        this.loadImage(element);
    }
  }

  // 懒加载图片
  loadImage(img) {
    const src = img.dataset.lazySrc;
    if (src) {
      // 创建新的图片对象预加载
      const newImg = new Image();
      newImg.onload = () => {
        img.src = src;
        img.classList.add('lazy-loaded');
        img.classList.remove('lazy-loading');
      };
      newImg.onerror = () => {
        img.classList.add('lazy-error');
        img.classList.remove('lazy-loading');
      };
      img.classList.add('lazy-loading');
      newImg.src = src;
    }
  }

  // 懒加载视频
  loadVideo(video) {
    const src = video.dataset.lazySrc;
    if (src) {
      video.src = src;
      video.load(); // 重新加载视频
      video.classList.add('lazy-loaded');
    }
  }

  // 懒加载背景图片
  loadBackground(element) {
    const src = element.dataset.lazySrc;
    if (src) {
      const img = new Image();
      img.onload = () => {
        element.style.backgroundImage = `url(${src})`;
        element.classList.add('lazy-loaded');
        element.classList.remove('lazy-loading');
      };
      element.classList.add('lazy-loading');
      img.src = src;
    }
  }

  // 观察元素
  observe(element) {
    if (this.observer) {
      this.observer.observe(element);
    } else {
      // 降级处理：直接加载
      this.loadElement(element);
    }
  }

  // 批量观察元素
  observeAll(selector = '[data-lazy-src]') {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => this.observe(element));
  }

  // 销毁观察器
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// 图片压缩工具
export const imageCompressor = {
  // 创建不同尺寸的图片URL
  createResponsiveUrl(originalUrl, width) {
    // 这里可以集成图片CDN服务，如阿里云OSS、腾讯云COS等
    // 示例：return `${originalUrl}?x-oss-process=image/resize,w_${width}`;
    
    // 如果没有CDN服务，可以根据width返回不同的预处理图片
    if (width <= 480) {
      return originalUrl.replace(/\.(jpg|jpeg|png)$/i, '_small.$1');
    } else if (width <= 768) {
      return originalUrl.replace(/\.(jpg|jpeg|png)$/i, '_medium.$1');
    } else if (width <= 1024) {
      return originalUrl.replace(/\.(jpg|jpeg|png)$/i, '_large.$1');
    }
    
    return originalUrl;
  },

  // 根据设备像素比选择合适的图片
  getOptimalImageUrl(baseUrl, sizes = [480, 768, 1024, 1920]) {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const screenWidth = window.innerWidth * devicePixelRatio;
    
    // 找到最接近的尺寸
    const optimalSize = sizes.find(size => size >= screenWidth) || sizes[sizes.length - 1];
    
    return this.createResponsiveUrl(baseUrl, optimalSize);
  }
};

// 预加载关键资源
export const preloader = {
  // 预加载图片
  preloadImages(urls) {
    return Promise.all(
      urls.map(url => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
          img.src = url;
        });
      })
    );
  },

  // 预加载视频
  preloadVideo(url) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.oncanplaythrough = () => resolve(url);
      video.onerror = () => reject(new Error(`Failed to load video: ${url}`));
      video.src = url;
      video.load();
    });
  },

  // 预加载关键资源
  async preloadCriticalResources() {
    const criticalImages = [
      '/src/assets/img/logo.png',
      '/src/assets/img/01.png',
      '/src/assets/img/02.png'
    ];

    try {
      await this.preloadImages(criticalImages);
      console.log('Critical resources preloaded');
    } catch (error) {
      console.warn('Some critical resources failed to preload:', error);
    }
  }
};

// 创建全局实例
const lazyLoader = new LazyLoader();

export default lazyLoader; 